import { useState } from 'react';
import { ThemeColors } from './ProjectThemeEditor';

interface Props {
  theme: ThemeColors;
  logoUrl: string | null;
}

const DEFAULT_THEME: ThemeColors = {
  primary: '#1B3A54',
  secondary: '#264E6E',
  tertiary: '#3B82F6',
  onPrimary: '#FFFFFF',
  surfaceAccent: '#E8EEF3',
};

const hexToRgba = (hex: string, alpha: number): string => {
  const h = hex.replace('#', '');
  if (h.length !== 6) return hex;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// ─── Phone frame ─────────────────────────────────────────────────────────────

const PhoneFrame = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="flex flex-col items-center">
    <span className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500">{title}</span>
    <div className="relative h-[390px] w-[195px] overflow-hidden rounded-[30px] border-[7px] border-gray-900 bg-white shadow-lg">
      <div className="absolute left-1/2 top-1 z-20 h-2.5 w-14 -translate-x-1/2 rounded-full bg-gray-900" />
      <div className="relative h-full w-full overflow-hidden bg-gray-50">{children}</div>
    </div>
  </div>
);

// ─── Shared pieces ───────────────────────────────────────────────────────────

const BottomTabBar = ({ theme, active }: { theme: ThemeColors; active: 'home' | 'profile' }) => {
  const tabs = [
    { key: 'home', label: 'Home' },
    { key: 'attendance', label: 'Att.' },
    { key: 'grooming', label: 'Groom' },
    { key: 'leave', label: 'Leave' },
    { key: 'tasks', label: 'Tasks' },
    { key: 'profile', label: 'Profile' },
  ];
  return (
    <div className="absolute bottom-0 left-0 right-0 flex h-9 items-center justify-around border-t border-gray-200 bg-white">
      {tabs.map((t) => {
        const isActive = t.key === active;
        return (
          <div key={t.key} className="flex flex-col items-center gap-0.5">
            <div
              className="flex h-5 w-5 items-center justify-center rounded-md"
              style={{
                backgroundColor: isActive ? theme.primary : 'transparent',
              }}
            >
              <div
                className="h-2 w-2 rounded-sm"
                style={{ backgroundColor: isActive ? theme.onPrimary : '#9CA3AF' }}
              />
            </div>
            <span
              className="text-[6px] font-semibold"
              style={{ color: isActive ? theme.primary : '#9CA3AF' }}
            >
              {t.label}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// ─── Home ────────────────────────────────────────────────────────────────────

const HomeFrame = ({ theme, logoUrl }: { theme: ThemeColors; logoUrl: string | null }) => (
  <div className="flex h-full w-full flex-col bg-gray-50">
    <div
      className="rounded-b-[18px] px-3 pt-4 pb-4"
      style={{
        background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.secondary} 100%)`,
      }}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] font-bold" style={{ color: theme.onPrimary }}>
            Good afternoon, Sangeeta
          </div>
          <div
            className="mt-0.5 text-[7px]"
            style={{ color: hexToRgba(theme.onPrimary, 0.8) }}
          >
            Tuesday, April 14, 2026
          </div>
        </div>
        {/* Match DynamicHomeScreen.tsx — plain contained image directly on the
            gradient header. No rounded badge, no translucent backdrop. Lets
            transparent PNGs render honestly so admins see what the app shows. */}
        {logoUrl && (
          <img src={logoUrl} alt="" className="h-6 w-6 flex-shrink-0 object-contain" />
        )}
      </div>
    </div>

    <div className="mt-2 px-2">
      <div className="rounded-lg bg-white p-2 shadow-sm">
        <div className="flex items-center justify-between">
          <div
            className="flex items-center gap-1 rounded-full px-1.5 py-0.5"
            style={{ backgroundColor: theme.surfaceAccent }}
          >
            <div className="h-1 w-1 rounded-full" style={{ backgroundColor: theme.tertiary }} />
            <span className="text-[6px] font-bold" style={{ color: theme.primary }}>
              Day In Progress
            </span>
          </div>
          <span className="text-[6px] text-gray-500">Tue, Apr 14</span>
        </div>

        <div className="mt-1.5 flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-1">
              <div className="h-1.5 w-1.5 rounded-full bg-green-500" />
              <div>
                <div className="text-[5px] text-gray-500">Signed In</div>
                <div className="text-[7px] font-bold text-gray-900">11:03 AM</div>
              </div>
            </div>
            <div className="ml-[3px] h-2 w-px bg-gray-200" />
            <div className="flex items-center gap-1">
              <div className="h-1.5 w-1.5 rounded-full bg-gray-300" />
              <div>
                <div className="text-[5px] text-gray-400">Signed Out</div>
                <div className="text-[7px] font-bold text-gray-400">--:-- --</div>
              </div>
            </div>
          </div>
          <div
            className="h-8 w-8 flex-shrink-0 rounded-md"
            style={{ backgroundColor: hexToRgba(theme.primary, 0.1) }}
          />
        </div>

        <div className="mt-1.5 border-t border-gray-100 pt-1.5">
          <div className="flex items-center gap-1">
            <div
              className="h-1.5 w-1.5 rounded-full"
              style={{ backgroundColor: theme.primary }}
            />
            <span className="text-[7px] font-bold text-gray-900">Mumbai-Fort</span>
          </div>
          <div className="ml-2 text-[5px] text-gray-500">Elphinstone Building, Mumbai</div>
        </div>
      </div>

      <div className="mt-1.5 flex items-center gap-1.5 rounded-lg bg-white p-1.5 shadow-sm">
        <div
          className="flex h-5 w-5 items-center justify-center rounded-md"
          style={{ backgroundColor: theme.primary }}
        >
          <div className="h-2 w-1.5 rounded-sm" style={{ backgroundColor: theme.onPrimary }} />
        </div>
        <div className="flex-1">
          <div className="text-[7px] font-bold text-gray-900">Complete Tasks</div>
          <div className="text-[5px] text-gray-500">4 tasks available</div>
        </div>
        <span className="text-[8px] text-gray-400">⌄</span>
      </div>

      <div
        className="mt-1.5 flex h-6 items-center justify-center gap-1 rounded-lg"
        style={{ backgroundColor: theme.primary }}
      >
        <span className="text-[9px]" style={{ color: theme.onPrimary }}>☾</span>
        <span className="text-[8px] font-semibold" style={{ color: theme.onPrimary }}>
          End Your Day
        </span>
      </div>
    </div>

    <BottomTabBar theme={theme} active="home" />
  </div>
);

// ─── Container ───────────────────────────────────────────────────────────────

export default function ProjectMobilePreview({ theme, logoUrl }: Props) {
  const [themeMode, setThemeMode] = useState<'current' | 'default'>('current');
  const activeTheme = themeMode === 'default' ? DEFAULT_THEME : theme;

  return (
    <div className="sticky top-0 rounded-xl border border-gray-200 bg-white p-4">
      <div className="mb-3 flex justify-center">
        <div className="inline-flex overflow-hidden rounded-md border border-gray-300">
          <button
            type="button"
            onClick={() => setThemeMode('current')}
            className={`px-2.5 py-1 text-xs font-medium transition-colors ${
              themeMode === 'current'
                ? 'bg-primary-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            Current
          </button>
          <button
            type="button"
            onClick={() => setThemeMode('default')}
            className={`border-l border-gray-300 px-2.5 py-1 text-xs font-medium transition-colors ${
              themeMode === 'default'
                ? 'bg-primary-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            Default
          </button>
        </div>
      </div>
      <div className="flex justify-center">
        <PhoneFrame title="Home">
          <HomeFrame theme={activeTheme} logoUrl={logoUrl} />
        </PhoneFrame>
      </div>
    </div>
  );
}
